import type { SearchFieldProps } from "react-aria-components";

import type { ReactNode } from "react";

import { memo, useEffect, useRef, useState } from "react";

import clsx from "clsx";
import { Button, Input, SearchField } from "react-aria-components";

import { validateHasValue as hasValue } from "@shared/utils";

import { CloseOutlineFilled } from "../../icons/close-outline-filled";
import { SearchIcon } from "../../icons/search";

import styles from "./searchbar.module.css";

const DEFAULT_DEBOUNCE_DELAY_MS = 300;

type PlaceholderProps = {
	ariaLabel?: string;
	className?: string;
	deboundeDelayMs?: number;
	onChange: (value: string) => void;
	placeholder: string;
	value: string;
} & Partial<SearchFieldProps>;

export const Searchbar = memo(function CustomSearchbar({
	ariaLabel = "Search",
	autoFocus = false,
	className,
	deboundeDelayMs = DEFAULT_DEBOUNCE_DELAY_MS,
	onChange,
	placeholder,
	value,
	...props
}: PlaceholderProps): ReactNode {
	const [query, setQuery] = useState(value);
	const onChangeRef = useRef(onChange);
	const timeoutRef = useRef<null | ReturnType<typeof setTimeout>>(null);

	useEffect(() => {
		onChangeRef.current = onChange;
		if (hasValue(value)) {
			setQuery(value);
		}
	}, [onChange, value]);

	// Cleanup pending debounce on unmount
	useEffect(() => {
		return () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}
		};
	}, []);

	return (
		<SearchField
			aria-label={ariaLabel}
			{...props}
			className={clsx(styles.searchbar, className)}
			onChange={(value) => {
				setQuery(value);

				if (timeoutRef.current) {
					clearTimeout(timeoutRef.current);
				}

				timeoutRef.current = setTimeout(() => {
					onChangeRef.current(value);
				}, deboundeDelayMs);
			}}
			value={query}
		>
			<SearchIcon className={styles.searchIcon} height={20} width={20} />
			<Input
				autoFocus={autoFocus}
				className={clsx(styles.input, "body-2")}
				placeholder={placeholder}
			/>
			{query && (
				<Button className={clsx(styles.closeButton)}>
					<CloseOutlineFilled />
				</Button>
			)}
		</SearchField>
	);
});
